import { Skeleton } from '@/client/components/ui/skeleton';
import useMedia from 'react-use/lib/useMedia';

const EventHeaderSkeleton = () => (
  <>
    <Skeleton className="w-16 h-4 mb-2" />
    <div className="flex flex-1 items-center space-x-3 md:space-x-6">
      <Skeleton className="w-[59px] h-[59px] md:w-[97px] md:h-[97px] rounded-2 flex-shrink-0" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-6 w-1/2" />
        <div className="flex gap-x-4">
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-20 h-4" />
        </div>
      </div>
    </div>
  </>
);

export const ChartSkeleton = () => (
  <div className="space-y-3">
    <div className="flex gap-2">
      <Skeleton className="w-8 h-6 rounded-full" />
      <Skeleton className="w-8 h-6 rounded-full" />
      <Skeleton className="w-8 h-6 rounded-full" />
      <Skeleton className="w-8 h-6 rounded-full" />
      <Skeleton className="w-8 h-6 rounded-full" />
    </div>
    <Skeleton className="h-[295px] w-full" />
  </div>
);

const OutcomeListSkeleton = () => (
  <div className="border border-foreground/4 rounded-3 overflow-hidden">
    <div className="hidden md:flex justify-between items-center px-6 py-5 bg-foreground/5 rounded-t-3">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="divide-y-1 divide-foreground/4">
      {[0, 1, 2].map(i => (
        <div key={i} className="p-6 flex items-center justify-between flex-wrap gap-6">
          <div className="flex-1 flex space-x-3 items-center">
            <Skeleton className="size-9 rounded-1 flex-shrink-0" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-7 w-12" />
          <div className="flex gap-3">
            <Skeleton className="h-12 w-32 rounded-2" />
            <Skeleton className="h-12 w-32 rounded-2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RulesSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-6 w-24" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-5/6" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

export const TradesSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-6 w-20" />
    <div className="border border-foreground/4 rounded-3 overflow-hidden divide-y-1 divide-foreground/4">
      {[0, 1, 2].map(i => (
        <div key={i} className="p-4 flex items-center gap-3">
          <Skeleton className="size-8 rounded-full flex-shrink-0" />
          <Skeleton className="h-4 w-24" />
          <div className="flex-1" />
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  </div>
);

// Not drawn on the event page: #344 removed its opinions block (BON-248). Kept with the component files.
export const OpinionsSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-6 w-32" />
    <Skeleton className="h-12 w-full rounded-3" />
    {[0, 1].map(i => (
      <div key={i} className="flex gap-3 items-start">
        <Skeleton className="size-8 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

export const TradePanelSkeleton = () => (
  <div className="w-full md:w-[383px] p-3">
    <div className="md:border-.5 md:border-foreground/20 md:p-4 md:rounded-6 space-y-6">
      <Skeleton className="h-10 w-full rounded-3" />
      <Skeleton className="h-14 w-full rounded-3" />
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <Skeleton className="h-12 w-full rounded-3" />
      <Skeleton className="h-12 w-full rounded-3" />
    </div>
  </div>
);

export const EventPageSkeleton = () => {
  const isDesktop = useMedia('(min-width: 768px)');

  return (
    <section className="px-4 pt-4 md:px-5 md:pt-6 pb-24 md:pb-10 w-full min-h-full flex justify-center items-start">
      <div className="flex-1 overflow-hidden md:max-w-[1000px] space-y-4 md:space-y-9 md:p-6">
        <EventHeaderSkeleton />
        <ChartSkeleton />
        <OutcomeListSkeleton />
        <RulesSkeleton />
        <TradesSkeleton />
      </div>
      {isDesktop && (
        <div className="flex-shrink-0 w-full p-3 md:w-[383px] pos-sticky top-15 h-[calc(100%-3.75rem)] z-10 overflow-y-auto">
          <TradePanelSkeleton />
        </div>
      )}
    </section>
  );
};
